import {Component, signal, inject, input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DialogModule} from 'primeng/dialog';
import {ButtonModule} from 'primeng/button';
import {DataTableComponent, TableColumn, TableConfig} from '@components/data-table/data-table.component';
import {TemplateFieldTranslatePipe} from '@app/utils/pipes/template-field-translate.pipe';
import {FullscreenImageMeta, handleErrors, openImageFullscreen} from '@app/utils/_msf';
import {NotReadyApiService} from '@app/api/not-ready-api-service';
import {HttpEventType} from '@angular/common/http';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {ServerTranslateService} from '@services/server-translate.service';
import {Loading} from '@components/ui/loading/loading';
import {DownloadProgressComponent} from '@components/ui/download-progress/download-progress.component';
import {TemplateControl, TemplateStep, TemplateBlock} from './request-result-modal.types';
import {Subject, takeUntil} from 'rxjs';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { UserService } from '@6grain/masofa-monolith-api-client/identity';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { LanguageMapper } from './utils/language-mapper.util';
import { PolygonExtractor } from './utils/polygon-extractor.util';
import { ZipExtractor } from './utils/zip-extractor.util';
import { ControlEnricher } from './services/control-enricher.service';
import { ControlValueService } from './services/control-value.service';
import { TableBuilderService } from './services/table-builder.service';
import { ControlValueTranslator } from './utils/control-value-translator.util';
import { blockHasFilledResults, stepHasFilledResults } from './utils/filled-result.util';
import {
  isAnomalySurveyReport,
  shouldShowAnomalySurveyReportBlock,
} from './utils/anomaly-survey-report-blocks.util';

@Component({
  selector: 'app-request-result-modal',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    DataTableComponent,
    Loading,
    DownloadProgressComponent
],
  templateUrl: './request-result-modal.component.html',
  styleUrls: ['./request-result-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestResultModalComponent implements OnDestroy {
  visible = signal(false);
  fileResultId = input<string>('');
  currentFileResultId = signal<string>('');
  downloading = signal(false);
  downloadProgress = signal(0);

  private readonly translateService = inject(TranslateService);
  private readonly notReadyApiService = inject(NotReadyApiService);
  private readonly dateFormatPipe = inject(DateFormatLangPipe);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly fieldService = inject(FieldService);
  private readonly userService = inject(UserService);
  private readonly cropService = inject(CropService);
  private readonly cdr = inject(ChangeDetectorRef);

  templateData: Record<string, unknown> | null = null;
  dynamicBlocks: TemplateBlock[] = [];
  nonStepsViews: Array<{ block: TemplateBlock; isAgro: boolean; isAnomalies: boolean }> = [];
  stepsBlocks: TemplateBlock[] = [];
  agrotechnicalMeasuresColumns: TableColumn[] = [];
  agrotechnicalMeasuresData: Record<string, string>[] = [];
  anomaliesColumns: TableColumn[] = [];
  anomaliesData: Record<string, string>[] = [];
  polygonJson: string | undefined = undefined;
  readonly stepTableConfig: TableConfig = { paginator: false, loading: false };

  private extractedFiles: Map<string, string | Blob> = new Map();
  private destroy$ = new Subject<void>();
  private downloadCancellation$ = new Subject<void>();
  private controlEnricher!: ControlEnricher;
  private controlValueService!: ControlValueService;

  constructor() {
    this.controlEnricher = new ControlEnricher(this.fieldService, this.userService, this.cropService);
    this.controlValueService = new ControlValueService(this.dateFormatPipe, this.serverTranslate);
  }

  open(fileResultId: string) {
    // Cancel any ongoing download
    this.cancelCurrentDownload();

    this.currentFileResultId.set(fileResultId);
    this.visible.set(true);
    this.downloadProgress.set(0);
    this.getBidZip(fileResultId);
  }

  close() {
    this.cancelCurrentDownload();
    this.visible.set(false);
    this.cleanupResources();
  }

  ngOnDestroy() {
    this.cleanupResources();
    this.downloadCancellation$.next();
    this.downloadCancellation$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cancelCurrentDownload(): void {
    this.downloadCancellation$.next();
  }

  private cleanupResources() {
    ZipExtractor.cleanupBlobUrls(this.extractedFiles);
    this.downloadProgress.set(0);
    this.downloading.set(false);
    this.templateData = null;
    this.currentFileResultId.set('');
    this.dynamicBlocks = [];
    this.nonStepsViews = [];
    this.stepsBlocks = [];
    this.agrotechnicalMeasuresColumns = [];
    this.agrotechnicalMeasuresData = [];
    this.anomaliesColumns = [];
    this.anomaliesData = [];
    this.polygonJson = undefined;
  }

  private loadData() {
    const templateJson = this.extractedFiles.get('template.json');
    if (templateJson && typeof templateJson === 'string') {
      try {
        const data = JSON.parse(templateJson) as Record<string, unknown>;
        this.templateData = data;
        this.processTemplateData(data);
      } catch (err) {
        console.error('Error parsing template.json:', err);
      }
    } else {
      console.error('template.json not found in extracted files');
    }
  }

  private getBidZip(fileId: string) {
    this.downloading.set(true);
    this.downloadProgress.set(0);

    this.notReadyApiService.downloadFileWithProgress(fileId).pipe(
      takeUntil(this.downloadCancellation$),
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (event) => {
        if (event.type === HttpEventType.DownloadProgress) {
          if (event.total) {
            this.downloadProgress.set(Math.round((event.loaded / event.total) * 100));
          }
        } else if (event.type === HttpEventType.Response) {
          this.downloadProgress.set(100);
          const blob = event.body as Blob;
          try {
            this.extractedFiles = await ZipExtractor.extractToMemory(blob, (progress) => {
              this.downloadProgress.set(progress);
            });
            this.loadData();
            this.downloading.set(false);
            this.cdr.markForCheck();
          } catch (error) {
            console.error('Error extracting zip file:', error);
            handleErrors(error);
            this.downloading.set(false);
            this.cdr.markForCheck();
          }
        }
      },
      error: (error) => {
        handleErrors(error);
        this.downloading.set(false);
        this.downloadProgress.set(0);
        this.cdr.markForCheck();
      }
    });
  }

  private async processTemplateData(data: Record<string, unknown>) {
    const blocks = this.getObjectProperty(data, 'blocks');
    this.dynamicBlocks = Array.isArray(blocks) ? (blocks as TemplateBlock[]) : [];
    this.rebuildBlockViews();
    this.extractAgrotechnicalMeasures(data);
    this.extractAnomaliesSurvey(data);
    await this.enrichControlValues();
    this.polygonJson = PolygonExtractor.extractFromBlocks(this.dynamicBlocks)
      ?? this.findBlockPolygonWkt(this.dynamicBlocks);
  }

  private rebuildBlockViews(): void {
    this.nonStepsViews = [];
    this.stepsBlocks = [];
    const bidWorkType = this.getObjectProperty(this.templateData, 'bidWorkType');
    const blockTypes = this.dynamicBlocks.map(block => this.getObjectProperty(block, 'blockType'));
    const anomalySurveyReport = isAnomalySurveyReport(bidWorkType, blockTypes);

    for (const block of this.dynamicBlocks) {
      const blockType = this.getObjectProperty(block, 'blockType');
      if (anomalySurveyReport && !shouldShowAnomalySurveyReportBlock(blockType)) {
        continue;
      }
      if (this.matchesEnumValue(blockType, 0, 'Steps')) {
        const filledSteps = Array.isArray(block.steps)
          ? block.steps.filter(step => stepHasFilledResults(step))
          : [];
        if (filledSteps.length > 0) {
          this.stepsBlocks.push({ ...block, steps: filledSteps });
        }
        continue;
      }

      const isAgro = this.matchesEnumValue(blockType, 5, 'AgrotechnicalMeasures');
      const isAnomalies = this.matchesEnumValue(blockType, 6, 'Anomalies');
      if (!isAgro && !isAnomalies && !blockHasFilledResults(block)) {
        continue;
      }

      this.nonStepsViews.push({
        block,
        isAgro,
        isAnomalies
      });
    }
  }

  private async enrichControlValues() {
    for (const block of this.dynamicBlocks) {
      if (block.controls) {
        for (const control of block.controls) {
          await this.controlEnricher.enrichControl(control);
          this.cdr.markForCheck();
        }
      }
      if (block.steps) {
        for (const step of block.steps) {
          for (const control of step.controls) {
            await this.controlEnricher.enrichControl(control);
          }
        }
      }
    }
  }

  getBlockTitle(block: TemplateBlock): string {
    const title = this.getTranslatedField(block, 'blockName');
    if (title) {
      return title;
    }
    return this.getLocalizedName(this.getObjectProperty(block, 'names'));
  }

  getStepTitle(step: TemplateStep): string {
    const title = this.getTranslatedField(step, 'name');
    if (title) {
      return title;
    }
    return this.getLocalizedName(this.getObjectProperty(step, 'names'));
  }

  getStepPhotoControl(step: TemplateStep): TemplateControl | null {
    return step.controls.find(control => this.isPhotoControl(control)) || null;
  }


  getStepTableColumns(step: TemplateStep): TableColumn[] {
    const lang = LanguageMapper.normalize(this.translateService.currentLang || 'en');
    const tableBuilder = new TableBuilderService(this.controlValueService, lang);
    return tableBuilder.buildStepTable(step).columns;
  }

  getStepTableData(step: TemplateStep): any[] {
    const lang = LanguageMapper.normalize(this.translateService.currentLang || 'en');
    const tableBuilder = new TableBuilderService(this.controlValueService, lang);
    return tableBuilder.buildStepTable(step).data;
  }

  getBlockTableColumns(block: TemplateBlock): TableColumn[] {
    const lang = LanguageMapper.normalize(this.translateService.currentLang || 'en');
    const tableBuilder = new TableBuilderService(this.controlValueService, lang);
    return tableBuilder.buildBlockTable(block).columns;
  }

  getBlockTableData(block: TemplateBlock): any[] {
    const lang = LanguageMapper.normalize(this.translateService.currentLang || 'en');
    const tableBuilder = new TableBuilderService(this.controlValueService, lang);
    const data = tableBuilder.buildBlockTable(block).data;
    this.fillBlankPolygonCell(block, data);
    return data;
  }

  getStepImagePath(photoControl: TemplateControl): string {
    return this.controlValueService.getImagePath(photoControl, this.extractedFiles);
  }

  getStepImagePaths(photoControl: TemplateControl): string[] {
    return this.controlValueService.getImagePaths(photoControl, this.extractedFiles, 5);
  }

  openImageFullscreen(imageUrl: string, imageUrls?: string[], startIndex = 0) {
    openImageFullscreen(imageUrl, imageUrls, startIndex);
  }

  openImageGallery(imageUrl: string): void {
    const galleryItems = this.getAllGalleryItems();
    const galleryImages = galleryItems.map(item => item.url);
    const galleryMeta: FullscreenImageMeta[] = galleryItems.map(item => ({ info: item.info }));
    const startIndex = galleryImages.findIndex(path => path === imageUrl);
    openImageFullscreen(imageUrl, galleryImages, startIndex >= 0 ? startIndex : 0, galleryMeta);
  }

  private extractAgrotechnicalMeasures(data: Record<string, unknown>): void {
    this.agrotechnicalMeasuresColumns = [];
    this.agrotechnicalMeasuresData = [];

    const blocks = this.getObjectProperty(data, 'blocks');
    if (!Array.isArray(blocks)) {
      return;
    }

    const agrotechnicalBlock = blocks.find(block =>
      this.matchesEnumValue(this.getObjectProperty(block, 'blockType'), 5, 'AgrotechnicalMeasures')
    );
    if (!agrotechnicalBlock) {
      return;
    }

    const controls = this.getObjectProperty(agrotechnicalBlock, 'controls');
    if (!Array.isArray(controls)) {
      return;
    }

    const repeaterControl = controls.find(control =>
      this.matchesEnumValue(this.getObjectProperty(control, 'type'), 11, 'Repeater')
    );
    if (!repeaterControl) {
      return;
    }

    const resultValues = this.getObjectProperty(repeaterControl, 'resultValues');
    if (!Array.isArray(resultValues) || resultValues.length === 0) {
      return;
    }

    const dictionaryMap = this.buildAgrotechnicalDictionaryMap(data);
    const repeaterControls = this.getObjectProperty(repeaterControl, 'repeaterControls');
    const columnLabels = this.buildRepeaterColumnLabels(repeaterControls);

    this.agrotechnicalMeasuresColumns = [
      { field: 'date', header: columnLabels.date, type: 'text' },
      { field: 'name', header: columnLabels.id, type: 'text' },
      { field: 'comment', header: columnLabels.comment, type: 'text' }
    ];

    this.agrotechnicalMeasuresData = resultValues.map(value => {
      const rawDate = this.asString(this.getObjectProperty(value, 'date'));
      const dateValue = rawDate
        ? (this.dateFormatPipe.transform(rawDate, LanguageMapper.normalize(this.translateService.currentLang || 'en'), false) || rawDate)
        : '-';
      const id = this.asString(this.getObjectProperty(value, 'id'));
      const dictionaryName = id ? (dictionaryMap.get(id.toLowerCase()) || id) : '-';
      const comment = this.asString(this.getObjectProperty(value, 'comment')) || '-';

      return {
        date: dateValue,
        name: dictionaryName,
        comment
      };
            }).filter(item => item.date !== '-' || item.name !== '-' || item.comment !== '-');
  }

  private extractAnomaliesSurvey(data: Record<string, unknown>): void {
    this.anomaliesColumns = [];
    this.anomaliesData = [];

    const blocks = this.getObjectProperty(data, 'blocks');
    if (!Array.isArray(blocks)) {
      return;
    }

    const anomaliesBlock = blocks.find(block =>
      this.matchesEnumValue(this.getObjectProperty(block, 'blockType'), 6, 'Anomalies')
    );

    const dictionaryMap = this.buildAnomaliesDictionaryMap(data);
    const rows = this.collectAnomalySurveyRows(data, anomaliesBlock);
    if (rows.length === 0) {
      return;
    }

    this.anomaliesColumns = [
      { field: 'name', header: this.translateService.instant('BID_ANOMALY_ON_MAP'), type: 'text' },
      { field: 'polygon', header: 'Polygon', type: 'text' },
      { field: 'comment', header: this.translateService.instant('Comment'), type: 'text' }
    ];

    this.anomaliesData = rows.map(value => {
      const id = this.asText(this.getObjectProperty(value, 'anomaly_id'))
        || this.asText(this.getObjectProperty(value, 'anomaly_dropdown'))
        || this.asText(this.getObjectProperty(value, 'fieldAnomalyId'));
      const typeText = this.asText(this.getObjectProperty(value, 'anomalyType'));
      const name = (id && dictionaryMap.get(id.toLowerCase()))
        || typeText
        || id
        || '-';
      const polygon = this.asText(this.getObjectProperty(value, 'anomaly_polygon'))
        || this.asText(this.getObjectProperty(value, 'polygonWkt'))
        || this.asText(this.getObjectProperty(value, 'polygon'))
        || '-';
      const comment = this.asText(this.getObjectProperty(value, 'anomaly_description'))
        || this.asText(this.getObjectProperty(value, 'comment'))
        || this.asText(this.getObjectProperty(value, 'description'))
        || '-';

      return { name, polygon, comment };
    }).filter(item => item.name !== '-' || item.polygon !== '-' || item.comment !== '-');
  }

  private collectAnomalySurveyRows(data: Record<string, unknown>, anomaliesBlock: unknown): Record<string, unknown>[] {
    const rootAnomalies = this.getObjectProperty(data, 'anomalies');
    if (Array.isArray(rootAnomalies) && rootAnomalies.length > 0) {
      return rootAnomalies.filter(row => !!row && typeof row === 'object') as Record<string, unknown>[];
    }

    const controls = this.getObjectProperty(anomaliesBlock, 'controls');
    if (!Array.isArray(controls)) {
      return [];
    }

    const rows: Record<string, unknown>[] = [];
    for (const control of controls) {
      if (!this.matchesEnumValue(this.getObjectProperty(control, 'type'), 11, 'Repeater')) {
        continue;
      }

      const resultValues = this.getObjectProperty(control, 'resultValues');
      if (!Array.isArray(resultValues)) {
        continue;
      }

      for (const row of resultValues) {
        if (row && typeof row === 'object') {
          rows.push(row as Record<string, unknown>);
        }
      }
    }

    return rows;
  }

  private buildAnomaliesDictionaryMap(data: Record<string, unknown>): Map<string, string> {
    const dictionaryData = this.getObjectProperty(data, 'dictionaryData');
    const map = new Map<string, string>();
    const catalogs = [
      this.getObjectProperty(dictionaryData, 'Anomalies'),
      this.getObjectProperty(dictionaryData, 'AnomaliesDictionary')
    ];

    for (const catalog of catalogs) {
      if (!Array.isArray(catalog)) {
        continue;
      }

      for (const dictionaryItem of catalog) {
        const id = this.asString(this.getObjectProperty(dictionaryItem, 'id'));
        const names = this.getObjectProperty(dictionaryItem, 'names');
        if (!id) {
          continue;
        }

        const localizedName = this.getLocalizedName(names) || id;
        map.set(id.toLowerCase(), localizedName);
      }
    }

    return map;
  }

  private fillBlankPolygonCell(block: TemplateBlock, data: any[]): void {
    const row = data?.[0];
    if (!row || typeof row !== 'object') {
      return;
    }

    const polygonKey = Object.keys(row).find(key => key.toLowerCase() === 'polygon');
    if (!polygonKey || !this.isBlankPolygonDisplay(row[polygonKey])) {
      return;
    }

    const fromBlock = this.asString(this.getObjectProperty(block, 'polygonWkt'));
    const fromAny = fromBlock || this.findBlockPolygonWkt(this.dynamicBlocks);
    if (fromAny) {
      row[polygonKey] = fromAny;
    }
  }

  private findBlockPolygonWkt(blocks: TemplateBlock[]): string | undefined {
    for (const block of blocks) {
      const wkt = this.asString(this.getObjectProperty(block, 'polygonWkt'));
      if (wkt && !this.isBlankPolygonDisplay(wkt)) {
        return wkt;
      }
    }
    return undefined;
  }

  private isBlankPolygonDisplay(value: unknown): boolean {
    if (value == null) {
      return true;
    }
    const text = String(value).trim();
    return text === '' || text === '-' || text === '[]' || text === 'null';
  }

  private buildAgrotechnicalDictionaryMap(data: Record<string, unknown>): Map<string, string> {
    const dictionaryData = this.getObjectProperty(data, 'dictionaryData');
    const agrotechnicalMeasures = this.getObjectProperty(dictionaryData, 'AgrotechnicalMeasures');
    const map = new Map<string, string>();

    if (!Array.isArray(agrotechnicalMeasures)) {
      return map;
    }

    for (const dictionaryItem of agrotechnicalMeasures) {
      const id = this.asString(this.getObjectProperty(dictionaryItem, 'id'));
      const names = this.getObjectProperty(dictionaryItem, 'names');
      if (!id) {
        continue;
      }

      const localizedName = this.getLocalizedName(names) || id;
      map.set(id.toLowerCase(), localizedName);
    }

    return map;
  }

  private buildRepeaterColumnLabels(repeaterControls: unknown): { date: string; id: string; comment: string } {
    const labels: { date: string; id: string; comment: string } = {
      date: 'Date',
      id: 'Agrotechnical measure',
      comment: 'Comment'
    };

    if (!Array.isArray(repeaterControls)) {
      return labels;
    }

    for (const control of repeaterControls) {
      const propertyName = this.asString(this.getObjectProperty(control, 'repeaterResultValuesPropertyName'))?.toLowerCase();
      if (!this.isAgrotechnicalColumnKey(propertyName)) {
        continue;
      }

      const labelValue = this.getControlLabel(control);
      if (labelValue) {
        labels[propertyName] = labelValue;
      }
    }

    return labels;
  }

  private getControlLabel(control: unknown): string {
    const labelsObject = this.getObjectProperty(control, 'labels');
    if (labelsObject && typeof labelsObject === 'object') {
      const localized = this.getLocalizedName(labelsObject);
      if (localized) {
        return localized;
      }
    }

    const lang = LanguageMapper.normalize(this.translateService.currentLang || 'en');
    const legacyLabel = this.getObjectProperty(control, `label_${lang}`);
    return this.asString(legacyLabel) || '';
  }

  private getLocalizedName(source: unknown): string {
    if (!source || typeof source !== 'object') {
      return '';
    }

    const names = source as Record<string, unknown>;
    const currentLang = this.translateService.currentLang || 'en-US';
    const normalizedLang = LanguageMapper.normalize(currentLang);
    const exact = this.asString(this.getObjectProperty(names, currentLang));
    if (exact) {
      return exact;
    }

    const localeCandidates = normalizedLang === 'ru'
      ? ['ru-RU', 'ru']
      : normalizedLang === 'uz'
        ? ['uz-Latn-UZ', 'uz']
        : ['en-US', 'en'];

    for (const candidate of localeCandidates) {
      const candidateValue = this.asString(this.getObjectProperty(names, candidate));
      if (candidateValue) {
        return candidateValue;
      }
    }

    for (const value of Object.values(names)) {
      if (typeof value === 'string' && value.trim() !== '') {
        return value;
      }
    }

    return '';
  }

  private getObjectProperty(source: unknown, key: string): unknown {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    const record = source as Record<string, unknown>;
    if (key in record) {
      return record[key];
    }

    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(record).find(existingKey => existingKey.toLowerCase() === lowerKey);
    return matchedKey ? record[matchedKey] : undefined;
  }

  private matchesEnumValue(value: unknown, numericValue: number, stringValue: string): boolean {
    if (typeof value === 'number') {
      return value === numericValue;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        return false;
      }

      if (trimmed.toLowerCase() === stringValue.toLowerCase()) {
        return true;
      }

      const parsedNumber = Number(trimmed);
      return !Number.isNaN(parsedNumber) && parsedNumber === numericValue;
    }

    return false;
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asText(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return '';
  }

  private isAgrotechnicalColumnKey(value: string): value is 'date' | 'id' | 'comment' {
    return value === 'date' || value === 'id' || value === 'comment';
  }

  private getAllGalleryItems(): Array<{ url: string; info: string }> {
    const itemsByUrl = new Map<string, { url: string; info: string }>();

    for (const block of this.dynamicBlocks) {
      const blockTitle = this.getTranslatedField(block, 'blockName') || this.getLocalizedName(this.getObjectProperty(block, 'names'));
      const blockInfo = this.buildControlsInfo(block.controls);
      const blockContext = this.composeInfoText(blockTitle, blockInfo);

      if (Array.isArray(block.controls)) {
        for (const control of block.controls) {
          if (this.isPhotoControl(control)) {
            const images = this.getStepImagePaths(control);
            for (const image of images) {
              if (!itemsByUrl.has(image)) {
                itemsByUrl.set(image, { url: image, info: blockContext });
              }
            }
          }
        }
      }

      if (Array.isArray(block.steps)) {
        for (const step of block.steps) {
          const stepTitle = this.getStepTitle(step);
          const stepInfo = this.buildControlsInfo(step.controls);
          const info = this.composeInfoText(blockTitle, blockInfo, stepTitle, stepInfo);
          if (Array.isArray(step.controls)) {
            for (const control of step.controls) {
              if (this.isPhotoControl(control)) {
                const images = this.getStepImagePaths(control);
                for (const image of images) {
                  if (!itemsByUrl.has(image)) {
                    itemsByUrl.set(image, { url: image, info });
                  }
                }
              }
            }
          }
        }
      }
    }

    return Array.from(itemsByUrl.values());
  }

  private getTranslatedField(source: unknown, prefix: 'blockName' | 'name'): string {
    const lang = LanguageMapper.normalize(this.translateService.currentLang || 'en');
    const langValue = this.asString(this.getObjectProperty(source, `${prefix}_${lang}`));
    if (langValue) {
      return langValue;
    }
    return this.asString(this.getObjectProperty(source, `${prefix}_en`));
  }

  private composeInfoText(...parts: Array<string | undefined>): string {
    return parts.filter(part => !!part && part.trim() !== '').join('\n');
  }

  private buildControlsInfo(controls: TemplateControl[] | undefined): string {
    if (!Array.isArray(controls)) {
      return '';
    }

    const lang = LanguageMapper.normalize(this.translateService.currentLang || 'en');
    const lines: string[] = [];

    for (const control of controls) {
      if (this.isPhotoControl(control) || control.type === 'polygon') {
        continue;
      }

      const label = ControlValueTranslator.getTranslatedLabel(control, lang) || control.name;
      const value = this.controlValueService.getValue(control, lang);
      if (!value || value === '-') {
        continue;
      }

      lines.push(`${label}: ${value}`);
    }

    return lines.join('\n');
  }

  private isPhotoControl(control: TemplateControl): boolean {
    return this.matchesEnumValue(control.type, 9, 'photo');
  }
}
